<template>
    <div class="relative">
        <div class="sticky top-0 z-50 bg-white w-full h-[152px]">
            <placement-header-alt
                :placement="visiblePlacementsNonColors?.[0]"
                :placementData="currentGroupActual"
                :index="0"
                :has-error="hasLocalErrorState"
                :hide-combine="true"
                :show-selections="true"
            >
                <div v-if="hasLocalErrorState"
                    class="text-red-error text-sm"
                >
                    {{ $t('SELECT_STYLE_NO_SHAFT_BAND') }}
                </div>
            </placement-header-alt>
            <div class="flex items-center justify-center gap-[10px] px-[20px] lg:px-[40px] border-b border-gray-DFDFDF py-[20px]">
                <black-white-button
                    :is-selected="selectedOption === 'stock'"
                    class="w-full"
                    @click="handleSelectStock"
                >
                    {{ $t('STOCK') }}
                </black-white-button>
                <black-white-button
                    :is-selected="selectedOption === 'custom'"
                    class="w-full flex items-center justify-center"
                    @click="handleSelectCustom"
                >
                    <crown-icon
                        :class="[
                            'mr-[8px]',
                            selectedOption === 'custom' ? 'text-white' : 'text-black'
                        ]"
                    />
                    {{ $t('CUSTOM') }}
                </black-white-button>
            </div>
        </div>
        <div class="pt-[29.73px]"></div>

        <div v-for="(placement) in visiblePlacementsNonColors"
            :key="placement.code"
            class="relative px-[20px] lg:px-[40px] flex flex-wrap justify-between"
        >
            <step-options-initials-buttons
                v-for="(option, i) in styleOptions(placement)"
                class="w-full"
                :key="option.code"
                :has-local-error="hasLocalErrorState"
                :main-placement="placement"
                :option="option"
                @unselect="handleSelectNothing"
                @select="selectActual"
                :index="i"
                :scroll-to-offset="180"
                :has-error-state="hasLocalErrorState"
                :is-full-width="false"
                :has-zoom-in="true"
                :zoom-image-size-percent="130"
                :has-checkmark-on-selected="true"
                :has-lock-icon="isLocked && selectedOption === 'custom'"
                :has-box-style-pricing="true"
                :show-free-upcharge-pricing="true"
            />

            <placement-standard :placement="placement"></placement-standard>
        </div>
    </div>
</template>

<script>
    import StepOptionsInitialsButtons from './buttons/StepOptionsInitialsButtons.vue';
    import PlacementHeaderAlt from './PlacementHeaderAlt.vue';
    import PlacementStandard from './PlacementStandard.vue';
    import allStores from '../stores/all';
    import BlackWhiteButton from './buttons/BlackWhiteButton.vue';
    import CrownIcon from './CrownIcon.vue';

    export default {
        mixins: [allStores],

        components: {
            StepOptionsInitialsButtons,
            PlacementHeaderAlt,
            PlacementStandard,
            BlackWhiteButton,
            CrownIcon,
        },

        props: {
            itemData: {
                type: Object,
                required: true,
            },
            isDirty: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                selectedOption: 'stock',
            };
        },

        watch: {
            hasLocalErrorState(val) {
                if (val) {
                    this.customizerStore.addPanelToErrorState('PutterShaftBand');
                } else {
                    this.customizerStore.removePanelFromErrorState('PutterShaftBand');
                }
            },

            selectedOption(newVal) {
                if (newVal === 'custom' && this.isLocked) {
                    this.showLockedToast();
                }
            },
        },

        computed: {
            hasLocalErrorState() {
                return this.isDirty && !this.isComplete;
            },

            visibleGroups() {
                return this.customizerStore.visibleGroups;
            },

            currentGroupActual() {
                return this.visibleGroups.find((group) => group.name === this.currentGroupName);
            },

            currentGroupName() {
                return this.customizerStore.currentGroup;
            },

            showGroupOverlay() {
                return this.appStore.showGroupOverlay;
            },

            panelToReset() {
                return this.customizerStore.panelToReset;
            },

            isDisabled() {
                return this.primarySelectedValue === 'Stamp-Stamp-Blank';
            },

            primarySelectedValue() {
                return this.visiblePlacementsNonColors?.[0]?.selectedValue;
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            visiblePlacements() {
                let placements = this.customizerStore.visiblePlacements(this.itemData) ?? [];

                const canColor = placements.some((placement) => placement.custom?.['v-allow-colors'] === 'yes');

                // Filter out colors.
                if (!canColor) {
                    placements = placements.filter((p) => {
                        if (p.custom?.['v-group-type'] === 'colors') {
                            return false;
                        }

                        return true;
                    });
                }

                return placements.map((placement) => {
                    const selected = this.viewController.selectedAtPlacement(placement.code);

                    // Add extra properties to each placement:
                    // - a code of the currently selected component.
                    return {
                        selectedValue: selected ? selected.code : null,
                        groupType: placement.custom?.['v-group-type'] ?? 'standard-buttons',
                        ...placement,
                    };
                });
            },

            visiblePlacementsNonColors() {
                return this.visiblePlacements.filter((x) => !x.code.includes('Color'));
            },

            visiblePlacementsColors() {
                return this.visiblePlacements.filter((x) => x.code.includes('Color'));
            },

            allowColors() {
                return this.visiblePlacements.some((placement) => placement.custom?.['v-allow-colors'] === 'yes');
            },

            isComplete() {
                return this.customizerStore.selectionsMade?.includes('Shaft Band');
            },

            isLocked() {
                return this.customizerStore.isGroupLocked('Shaft Band');
            },
        },

        methods: {
            handleSelectStock() {
                this.selectedOption = 'stock';
            },

            handleSelectCustom() {
                this.selectedOption = 'custom';
            },

            setShowGroupOverlay(value) {
                this.appStore.setShowGroupOverlay(value);
            },

            handleApplySelections() {
                if (!this.isComplete) {
                    return;
                }
                this.appStore.setShowGroupOverlay(false);
                this.$emit('applySelections');
            },

            getPlacementVariant(placement) {
                return placement?.custom?.['v-placement-variant'];
            },

            selectValue(placement, option) {
                this.customizerStore.setSelection(placement.code, option.value, false);
            },

            selectedValue(placement) {
                return placement.selectedValue;
            },

            selectedPlaceholder(placement) {
                return placement.selectedPlaceholder;
            },

            styleOptions(placement) {
                const options = this.customizerStore.sortOptions(placement, this.selectedValue(placement));

                // On putters, only show the default assembly option and default size option.
                const filtered = [];

                // eslint-disable-next-line no-restricted-syntax
                for (const o of options) {
                    const isCustomShop = o.raw.custom['v-custom-shop'] === 'true';

                    if (this.selectedOption === 'custom') {
                        if (!isCustomShop) {
                            // eslint-disable-next-line no-continue
                            continue;
                        }
                    }

                    if (this.selectedOption === 'stock') {
                        if (isCustomShop) {
                            // eslint-disable-next-line no-continue
                            continue;
                        }
                    }

                    filtered.push(o);
                }

                return filtered;
            },

            handleSelectNothing() {
                const placement = this.visiblePlacementsNonColors?.[0];

                if (placement) {
                    const placeholder = placement.components.find(
                        (x) => x.placeholder
                            && this.viewController.isAvailable(x)
                            && !x.placeholderOptional,
                    );

                    if (placeholder) {
                        this.viewController.updateComponent(placement.code, placeholder.code);
                    }
                }
            },

            showLockedToast() {
                this.appStore.showToast({
                    icon: 'info',
                    message: this.$t('GROUP_LOCKED_TOAST_SHAFT_BAND'),
                    linkText: this.$t('CUSTOMIZE'),
                    linkAction: () => {
                        this.appStore.setShowGroupOverlay(false);
                    },
                });
            },

            selectionIsLocked(placementCode, optionCode) {
                const placement = this.viewController.findPlacement(placementCode);
                const option = placement?.components?.find((component) => component.code === optionCode);

                return this.isLocked && option?.custom?.['v-custom-shop'] === 'true';
            },

            selectActual({ placement, option }) {
                if (this.selectionIsLocked(placement, option)) {
                    this.showLockedToast();
                    return;
                }

                this.viewController.updateComponent(placement, option);
            },
        },
    };
</script>
